import { Button, Card, Col, message, Row, Space, Tag, Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { globalNotificationState } from "../../../../../../_common/global_state";
import { convertToUppercase, useIsMobile } from "../../../../../../_common/function";
import { useSubmitUserVerificationHistory } from "../../../../function/accountVerification";
import { verificationRefetchState } from "../../../../state";
import { extractLocalizedValue, getStatusColor, isDocumentFilled, isStepFilled } from "../../../../common";
import { VerificationStep, isPendingLikeStatus } from "../../../../common/constant";
import { apiGetQuestionnaireRecordStatus } from "../../../../../../api/restful/questionnaire";
import { validateTierDocuments, buildSubmitInput, collectRequiredQuestionnaires } from "../../../../lib/submit";

function SelectDocument(props) {
    const {
        currentHistory,
        selectedTier,
        setSelectedDocument,
        setSelectedInnerDocument,
        selectedDocument,
        isInnerDocument,
        goNext,
        goBack,
        goTo,
        userVerificationHistories,
        tierSettingNextTierExist
    } = props

    const isMobile = useIsMobile()
    const { t } = useTranslation();
    const setNotificationMessage = useSetRecoilState(globalNotificationState)

    const { submitUserVerificationHistory } = useSubmitUserVerificationHistory();
    const { refetchUserVerificationHistories } = useRecoilValue(verificationRefetchState);

    const isPendingVerification = isPendingLikeStatus(currentHistory?.status)

    const tierDocuments = isInnerDocument ? selectedDocument?.steps?.map(setting => ({
        ...setting.sourceDoc,
        isRequired: setting.isRequired,
    })) : selectedTier?.documentSettings?.map(
        setting => ({
            ...setting.documentSettingDoc,
            isRequired: setting.isRequired,
        })
    ) || [];

    const handleSelectDocument = (document) => {
        if (isInnerDocument) {
            setSelectedInnerDocument(document)
            goNext()
        } else {
            setSelectedInnerDocument(null)
            setSelectedDocument(document)
            goNext()

            if (document?.steps?.[0]?.sourceType === "field_setting") {
                goNext()
            }
        }
    }

    const handleSubmitVerification = async () => {
        if (!currentHistory?._id) return;

        // required questionnaires (incl. nested in document groups) must be submitted first
        const requiredQuestionnaires = collectRequiredQuestionnaires(selectedTier?.documentSettings, currentHistory.documents);
        for (const q of requiredQuestionnaires) {
            if (!q.recordId) {
                message.error(t("errorMsg.required.pleaseFillIn", { field: q.fieldName }));
                return;
            }
            try {
                const response = await apiGetQuestionnaireRecordStatus(q.recordId);
                const isSubmitted = response?.status === 'success' && response.data === true;
                if (!isSubmitted) {
                    message.error(`${t("Please submit the questionnaire first")}`);
                    return;
                }
            } catch (error) {
                console.error("Error checking questionnaire status", error?.message);
                message.error(`${t("Please submit the questionnaire first")}`);
                return;
            }
        }

        const documents = currentHistory.documents || [];

        // validate required docs + numberOfRequired ("any N of M") before submit
        const validation = validateTierDocuments(selectedTier, documents);
        if (!validation.ok) {
            message.error(t(validation.messageKey, validation.messageParams));
            return;
        }

        try {
            const input = buildSubmitInput(currentHistory, documents);

            const result = await submitUserVerificationHistory(input)

            if (result?.status === "Success") {
                await refetchUserVerificationHistories();

                setNotificationMessage({
                    status: "success",
                    title: "",
                    msg: t("accountVerification.submitSuccessfully"),
                    key: "accountVerificationNotification",
                });

                // Always the tier list, never the generic "under review"
                // screen. The list carries a status tag per tier, so the client
                // sees the item they just submitted marked pending alongside
                // whatever is still outstanding or already verified — which one
                // sentence cannot express now a case can ask for several
                // standalone verifications at once.
                //
                // This, with the matching change in resolveInitialStep, is what
                // leaves VerificationStep.Status unreachable. Restore both to
                // bring the screen back.
                goTo(VerificationStep.Tier)
            }
        } catch (error) {
            console.error("handleSubmitTier: ", error)
            message.error(t("accountVerification.systemError"))
        }
    }

    return (
        <Row gutter={[16, 16]}>
            {tierDocuments?.map((document) => {
                const displayName = extractLocalizedValue(document.displayName) || document.name;
                const description = extractLocalizedValue(document.description);

                const tierHistory = [...(userVerificationHistories || [])].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).find(
                    (h) => (h.tierSetting?._id || h.tierSetting) === selectedTier._id
                );

                const submittedDoc = isDocumentFilled(tierHistory?.documents?.find((d) => d._id === document._id))

                const isSubmittedInnerDoc = isInnerDocument ? isStepFilled(tierHistory?.documents?.find(
                    (d) => d._id === selectedDocument?._id
                )?.steps?.find(d => d?.sourceId === document._id)) : null

                const status = (submittedDoc || isSubmittedInnerDoc) ? tierHistory.status : null;
                const color = status ? getStatusColor(status) : undefined

                return (
                    <Col span={24} key={document._id} className={"mh-100"}>
                        <Card className={`account-verification-card-selection h-full`}>
                            <div className="account-verification-card-selection-title">
                                {
                                    document.isRequired
                                    &&
                                    (
                                        <Tooltip title={"This section is required"}>
                                            <span className="account-verification-required-mark">
                                                *
                                            </span>
                                        </Tooltip>
                                    )
                                }
                                {displayName}
                                {
                                    status ? <Tag className={"ms-2"} color={color}
                                        bordered={false}>{convertToUppercase(status.replace(/_/g, " "))}</Tag> : <></>
                                }
                            </div>

                            <p className={"mb-2"}><small>{description}</small></p>

                            <Row justify={"end"} className={"mt-auto"}>
                                <Button
                                    type={"default"}
                                    className="statusButton"
                                    onClick={() => handleSelectDocument(document)}
                                //loading={buttonState[document?.document?._id] || false}
                                >
                                    {t("common.next")}
                                </Button>
                            </Row>
                        </Card>
                    </Col>
                );
            })}

            <Row justify={"end"} className={"w-full mt-6"}>
                <Space style={{ display: "flex", justifyContent: "flex-end" }}>
                    <Button onClick={goBack}>
                        Back
                    </Button>

                    {
                        !isPendingVerification
                        &&
                        (
                            <Button
                                type="primary"
                                onClick={handleSubmitVerification}
                                disabled={isPendingVerification}
                            >
                                Submit
                            </Button>
                        )
                    }
                </Space>
            </Row>
        </Row>
    )
}

export default SelectDocument;
